import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import SectionBadge from "../ui/SectionBadge";
import Slider from "../ui/Slider";
import Button from "../ui/Button";
import BrandLogo from "../ui/BrandLogos";
import { StarIcon, ArrowLeft02Icon } from "hugeicons-react";
import { useMobilePerformance } from "../../hooks/useMobilePerformance";

export default function BrandShowcase({ show = true }: { show?: boolean }) {
  if (!show) return null;
  const { shouldReduceBlur, shouldReduceAnimations } = useMobilePerformance();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const lightSweep = useTransform(scrollYProgress, [0, 1], ["-100%", "200%"]);
  const ambientOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0, 0.4, 0]);
  
  return (
    <section ref={sectionRef} className="py-24 md:py-32 bg-[#020000] relative overflow-hidden">
       {/* Seamless Masks */}
       <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-[#020000] to-transparent z-10 pointer-events-none" />
       <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#020000] to-transparent z-10 pointer-events-none" />
      
      {/* Red Ambient Glow - Optimized for mobile */}
      {!shouldReduceBlur && (
        <motion.div 
          style={{ opacity: ambientOpacity }}
          className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(220,20,60,0.15)_0%,transparent_70%)] pointer-events-none z-1" 
        />
      )}

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-8 md:mb-16"
          initial={shouldReduceAnimations ? { opacity: 0.9 } : { opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={shouldReduceAnimations ? { duration: 0.15 } : { duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex flex-col items-center mb-6 md:mb-12">
            <SectionBadge text="ماركات عالمية" icon={StarIcon} />
            
            <h2 className="text-3xl md:text-5xl lg:text-7xl font-black text-white mb-4 md:mb-6 tracking-tighter">
              جودة <span className="text-primary italic">نثق</span> بها
            </h2>
            
            <p className="text-white/60 text-center max-w-2xl mx-auto text-sm md:text-lg leading-relaxed px-4">
              لا نرضى إلا بالأفضل، لذلك اخترنا العمل مع قادة الصناعة عالمياً لضمان أفضل النتائج لسيارتك.
            </p>
          </div>
        </motion.div>

        {/* Brand Slider */}
        <div className="relative group/slider">
          <Slider
            autoSlideInterval={8000}
            showPagination={true}
            loop={true}
            className="max-w-7xl mx-auto"
            onSlideChange={() => {}}
          >
            {brandShowcaseData.map((brand) => (
              <BrandSlide key={brand.id} brand={brand} />
            ))}
          </Slider>
        </div>

        {/* Infinite Marquee Logos */}
        <motion.div
          className="mt-16 md:mt-32 pt-10 md:pt-20 border-t border-white/5"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="relative w-full overflow-hidden flex flex-col items-center">
             {/* Dynamic Light Sweep across the whole marquee area */}
             <motion.div 
                style={{ left: lightSweep }}
                className="absolute top-0 bottom-0 w-64 bg-gradient-to-r from-transparent via-primary/5 to-transparent skew-x-[-20deg] pointer-events-none z-20"
             />

             <p className="text-white/30 font-black text-[10px] mb-6 md:mb-12 tracking-[0.6em] uppercase">
                شركاء النجاح العالميين
             </p>
             
             {/* Brand Logos - Simple centered display */}
             {/* Brand Logos - Infinite Marquee with Auto-Scroll */}
             <div className="relative w-full overflow-hidden mask-linear-fade">
                <motion.div 
                  className="flex items-center gap-16 md:gap-32 w-max px-4"
                  initial={{ x: "-50%" }}
                  animate={{ x: "0%" }}
                  transition={{ 
                    duration: 40, 
                    ease: "linear", 
                    repeat: Infinity,
                    repeatType: "loop"
                  }}
                  whileHover={{ animationPlayState: "paused" }}
                >
                  {/* Triple duplication to ensure smooth loop on ultra-wide screens */}
                  {[...brandShowcaseData, ...brandShowcaseData, ...brandShowcaseData].map((brand, index) => (
                    <motion.div 
                      key={`${brand.id}-${index}`}
                      className="w-24 md:w-32 brightness-0 invert opacity-40 hover:opacity-100 transition-all duration-500 cursor-pointer flex-shrink-0"
                      whileHover={{ scale: 1.1, opacity: 1 }}
                    >
                      <BrandLogo brand={brand.id} size="large" />
                    </motion.div>
                  ))}
                </motion.div>
                
                {/* Gradient Masks for smooth fade edges */}
                <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#020000] to-transparent z-10 pointer-events-none" />
                <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#020000] to-transparent z-10 pointer-events-none" />
             </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function BrandSlide({ brand }: { brand: typeof brandShowcaseData[0] }) {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const imgScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.1, 1, 1.1]);

  return (
    <div ref={containerRef} className="px-2 md:px-8 py-4 md:py-8">
      <div className="relative bg-[#050505] rounded-[20px] md:rounded-[48px] overflow-hidden border border-white/5 group/card transition-all duration-500 hover:border-primary/20 hover:shadow-[0_40px_100px_rgba(0,0,0,0.8)]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 relative z-10">
          <div className="p-5 md:p-16 flex flex-col justify-center order-2 lg:order-1 text-right" dir="rtl">
            <motion.div
              className="mb-4 md:mb-8 brightness-0 invert flex items-center justify-start scale-75 md:scale-100 origin-right"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <BrandLogo brand={brand.id} size="default" />
            </motion.div>

            <motion.p
              className="text-primary font-black text-base md:text-xl mb-3 md:mb-6 uppercase tracking-[0.15em] md:tracking-[0.2em]"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              {brand.tagline}
            </motion.p>

            <motion.p
              className="text-gray-400 text-sm md:text-xl leading-relaxed mb-6 md:mb-10 opacity-80"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              {brand.description}
            </motion.p>

            <motion.div
              className="grid grid-cols-2 gap-y-2 md:gap-y-4 mb-6 md:mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              {brand.highlights.map((highlight, index) => (
                <div key={index} className="flex items-center gap-2 md:gap-3">
                  <div className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-primary shadow-[0_0_8px_rgba(220,20,60,1)]" />
                  <span className="text-gray-200 text-xs md:text-base font-bold">{highlight}</span>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <Button
                size="lg"
                variant="outline"
                className="group w-full md:w-auto hover:bg-white hover:text-black border-white/10"
                onClick={() => {
                  const element = document.querySelector(brand.ctaLink);
                  element?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <div className="flex items-center gap-3">
                  <ArrowLeft02Icon size={20} className="transition-transform group-hover:-translate-x-1" />
                  <span className="font-black text-sm uppercase tracking-widest">{brand.ctaText}</span>
                </div>
              </Button>
            </motion.div>
          </div>

          <div className="relative h-[200px] md:h-[350px] lg:h-auto overflow-hidden order-1 lg:order-2">
            <motion.div
              className="absolute inset-0"
              style={{ scale: imgScale }}
            >
              {/* Product Background Texture */}
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0)_0%,rgba(0,0,0,0.8)_100%)] z-10" />
              
              <img
                src={brand.productImage}
                alt={`${brand.name} collection`}
                className="w-full h-full object-cover opacity-80 group-hover/card:opacity-100 transition-opacity duration-700"
                loading="lazy"
              />
              
              {/* Luxury Light Sweep Reveal */}
              <motion.div 
                initial={{ left: "-100%" }}
                whileInView={{ left: "100%" }}
                viewport={{ once: true }}
                transition={{ duration: 1.5, ease: "easeInOut", delay: 0.5 }}
                className="absolute top-0 bottom-0 w-full bg-gradient-to-r from-transparent via-white/5 to-transparent skew-x-[-20deg] z-20"
              />
            </motion.div>

            {/* Accent Shadow */}
            <div 
              className="absolute inset-0 opacity-40 pointer-events-none" 
              style={{ background: `radial-gradient(circle at 70% 50%, ${brand.accentColor}33 0%, transparent 60%)` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * Premium brand showcase data
 * Using actual brand product images - showing products, not cars
 */
const brandShowcaseData = [
  {
    id: "stek",
    name: "STEK",
    tagline: "حماية طلاء بمعايير عالمية",
    description:
      "STEK هي شركة رائدة عالمياً في مجال حماية الطلاء. تتميز منتجاتها بتقنية الشفاء الذاتي المتطورة التي تضمن حماية مثالية لطلاء سيارتك من الخدوش والصدمات.",
    highlights: [
      "تقنية الشفاء الذاتي",
      "حماية من الأشعة فوق البنفسجية",
      "ضمان لمدة 5 سنوات",
      "مقاومة للاصفرار",
    ],
    productImage:
      "https://www.paintgard.com.au/cdn/shop/files/stek-logo-with-dynoshield-square-1_0a6fe8de-37f7-4daa-ae9b-5aea7499a3e0.jpg?v=1729813983&width=1400",
    accentColor: "#dc143c",
    ctaText: "اكتشف منتجات STEK",
    ctaLink: "#services",
  },
  {
    id: "koch-chemie",
    name: "Koch Chemie",
    tagline: "تقنيات ألمانية متقدمة",
    description:
      "Koch Chemie هي علامة ألمانية رائدة في صناعة منتجات العناية بالسيارات منذ عام 1968. تقدم حلولاً احترافية للتنظيف والتلميع والحماية بأعلى معايير الجودة الألمانية.",
    highlights: [
      "صناعة ألمانية أصلية",
      "خبرة أكثر من 55 عاماً",
      "منتجات صديقة للبيئة",
      "معتمدة من مصانع السيارات",
    ],
    productImage:
      "https://theragcompany.com/cdn/shop/products/kochchemie-spray-bottle-main-web.jpg?v=1762198106&width=1400",
    accentColor: "#E31E24",
    ctaText: "منتجات Koch Chemie",
    ctaLink: "#services",
  },
  {
    id: "tonyin",
    name: "Tonyin",
    tagline: "تقنية النانو سيراميك المتطورة",
    description:
      "Tonyin متخصصة في تقنيات النانو سيراميك المتقدمة، توفر طبقة حماية فائقة لطلاء السيارة مع لمعان استثنائي يدوم لسنوات. منتجات معتمدة دولياً بأعلى معايير الجودة.",
    highlights: [
      "تقنية النانو سيراميك",
      "لمعان يدوم سنوات",
      "حماية 9H+",
      "مقاومة للماء والأوساخ",
    ],
    productImage:
      "https://tonyinaustralia.com.au/cdn/shop/files/356f183f-7d97-40d7-8568-71fc4a44721b.png?v=1713921423&width=1400",
    accentColor: "#FFD700",
    ctaText: "استكشف Tonyin",
    ctaLink: "#services",
  },
];

